import React from 'react'

import {
  PolicyContent,
  PolicyIntro,
  PolicyList,
  PolicySection,
  PolicySectionTitle,
  PolicySubheading,
} from './styled'

export default function PrivacyPolicyContent() {
  return (
    <PolicyContent>
      <PolicyIntro>
        MagVenture is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share your personal information when you visit our website. By using our website, you agree to this Privacy Policy.
      </PolicyIntro>

      <PolicySection>
        <PolicySectionTitle>Information We Collect</PolicySectionTitle>
        <p>We may collect the following types of information:</p>

        <PolicySubheading>Personal Information</PolicySubheading>
        <p>This includes:</p>
        <PolicyList>
          <li>Name, email, phone number, mailing address.</li>
          <li>Account credentials if you create an account in our webshop.</li>
          <li>Payment details for purchases in our webshop.</li>
          <li>Any information you voluntarily provide when contacting us.</li>
        </PolicyList>

        <PolicySubheading>Device &amp; Usage Information</PolicySubheading>
        <p>We automatically collect:</p>
        <PolicyList>
          <li>IP address, browser type, device type, and operating system.</li>
          <li>Information on how you interact with our website (e.g., pages visited, time spent, referral sources).</li>
          <li>Cookies and tracking data (see Cookies &amp; Tracking below).</li>
        </PolicyList>

        <PolicySubheading>Health Information</PolicySubheading>
        <p>MagVenture does not collect protected health information (PHI) subject to HIPAA. If you are a healthcare provider, any data shared is used strictly for business interactions.</p>
      </PolicySection>

      <PolicySection>
        <PolicySectionTitle>How We Use Your Information</PolicySectionTitle>
        <p>We use your data to:</p>
        <PolicyList>
          <li>Provide and improve our products and services.</li>
          <li>Process payments and manage orders.</li>
          <li>Respond to inquiries and provide customer support.</li>
          <li>Send promotional content (you may opt-out anytime).</li>
          <li>Analyze website usage and improve user experience.</li>
        </PolicyList>
      </PolicySection>

      <PolicySection>
        <PolicySectionTitle>How We Store &amp; Protect Your Data</PolicySectionTitle>
        <PolicySubheading>Salesforce</PolicySubheading>
        <p>Your personal data is securely stored within Salesforce's cloud-based infrastructure, which adheres to leading industry standards, including:</p>
        <PolicyList>
          <li><strong>Encryption:</strong> Data is encrypted at rest and in transit to prevent unauthorized access.</li>
          <li><strong>Access Control:</strong> Only authorized personnel with specific permissions can access your personal data.</li>
          <li><strong>Data Centers:</strong> Salesforce data centers comply with ISO 27001, SOC 2, and other global security standards.</li>
        </PolicyList>
        <p>Salesforce implements reasonable security measures to protect your data, but please note that no system is 100% secure. If you suspect unauthorized access to your information, please contact us immediately.</p>

        <PolicySubheading>Our Webshop</PolicySubheading>
        <p>Our webshop utilizes WooCommerce extensions, which may collect additional personal data, such as city and zip code, and share relevant information with third-party services, such as billing details for payment processing.</p>
        <p>Depending on the extensions and settings enabled, certain customer data may be shared with external service providers, including payment gateways and analytics tools, to enhance your shopping experience and fulfill transactions securely.</p>
      </PolicySection>

      <PolicySection>
        <PolicySectionTitle>Sharing of Information</PolicySectionTitle>
        <p>MagVenture does not sell personal data. However, we may share information with:</p>
        <PolicyList>
          <li><strong>Service Providers:</strong> We work with trusted third parties to assist with payment processing, website hosting, analytics, and marketing.</li>
          <li><strong>Legal &amp; Compliance:</strong> We may disclose information if required by law, in response to legal requests, or to protect our rights.</li>
          <li><strong>Business Transfers:</strong> In case of a merger, acquisition, or sale, your information may be transferred to the new entity.</li>
        </PolicyList>
      </PolicySection>

      <PolicySection>
        <PolicySectionTitle>Compliance with U.S. Privacy Regulations</PolicySectionTitle>
        <p>We comply with applicable privacy laws, including but not limited to:</p>
        <PolicyList>
          <li><strong>California Consumer Privacy Act (CCPA):</strong> If you are a California resident, you have additional rights regarding your personal data, including the right to opt-out of data sharing.</li>
          <li><strong>Federal Trade Commission (FTC) Guidelines:</strong> We follow best practices for protecting consumer information from unauthorized access.</li>
          <li><strong>Gramm-Leach-Bliley Act (GLBA):</strong> If we collect financial data, we comply with GLBA data protection requirements.</li>
        </PolicyList>
      </PolicySection>

      <PolicySection>
        <PolicySectionTitle>Cookies &amp; Tracking Technologies</PolicySectionTitle>
        <p>MagVenture uses cookies and similar tracking technologies to enhance your experience.</p>
        <p>Types of cookies we use:</p>
        <PolicyList>
          <li><strong>Essential Cookies:</strong> Necessary for website functionality.</li>
          <li><strong>Analytics Cookies:</strong> Help us improve performance (Google Analytics, etc.).</li>
          <li><strong>Advertising Cookies:</strong> Used for personalized ads (third-party platforms).</li>
        </PolicyList>
        <PolicySubheading>How to Manage Cookies</PolicySubheading>
        <p>You can control cookie settings through your browser. Note that disabling cookies may affect website functionality.</p>
      </PolicySection>

      <PolicySection>
        <PolicySectionTitle>Data Retention &amp; Security</PolicySectionTitle>
        <p>We retain personal data as long as necessary for business operations or legal compliance. You are entitled to access and update your Personal Data, or request its deletion at any time. If you wish to access, amend or delete your Personal Data, please contact us at the address shown below. MagVenture will address your request promptly and free of charge.</p>
        <p>Requests to delete Personal Data will be subject to any applicable legal and ethical reporting or document filing or retention obligations with which MagVenture must comply. In all other cases, MagVenture will keep Personal Data only as long as is reasonably required for business purposes related to the basis for collection of the Personal Data.</p>
        <p>Our services are not intended for children under 18. We do not knowingly collect personal data from minors.</p>
      </PolicySection>

      <PolicySection>
        <PolicySectionTitle>Social Media &amp; Third-Party Services</PolicySectionTitle>
        <p>Our website may include social media features (Facebook, LinkedIn, etc.). These third-party platforms have their own privacy policies, and MagVenture is not responsible for their practices.</p>
      </PolicySection>

      <PolicySection>
        <PolicySectionTitle>Changes to This Privacy Policy</PolicySectionTitle>
        <p>We may update this policy from time to time. This version was updates on March 19, 2025.</p>
      </PolicySection>

      <PolicySection>
        <PolicySectionTitle>Data Protection Officer</PolicySectionTitle>
        <p>We have appointed a Data Protection Officer who can always be contacted by email, post or telephone if you have any questions about the way we process your personal data. The Data Protection Officer can be contacted at the following address:</p>
        <address>
          <strong>Danish Data Protection Agency</strong><br />
          Christian C. Bürger – CTO<br />
          Email: <a href="mailto:ccb@magventure.com">ccb@magventure.com</a><br />
          Address: Lucernemarken, 15 – Farum, Denmark<br />
          Phone: <a href="tel:+452366752">+452366752</a>
        </address>
      </PolicySection>
    </PolicyContent>
  )
}
